'use client';

import { forwardRef } from 'react';
import { Box } from '@highlight-cp/core-ui';

import classes from './count-pill.module.css';

export interface CountPillProps {
  /** Pill label content */
  children: React.ReactNode;
  /** Optional count shown in a circular badge */
  count?: number;
  /** `subtle` drops the chip fill, leaving the label and its count badge. */
  variant?: 'filled' | 'subtle';
  /** `sm` shrinks the label and the count badge. Defaults to `md`. */
  size?: 'md' | 'sm';
}

/**
 * ### CountPill
 *
 * @description
 * A compact label chip with an optional circular count badge — used for stat-style tags such as
 * demographic breakdowns and hidden-variable keys. Unrelated to Mantine's `Pill`: for a removable
 * tag, use `Pill` from `@highlight-cp/core-ui`.
 *
 * @example
 * ```tsx
 * <CountPill>Panel Size</CountPill>
 * <CountPill count={42}>Panel Size</CountPill>
 * <CountPill count={42} variant="subtle">Panel Size</CountPill>
 * <CountPill count={42} size="sm">Panel Size</CountPill>
 * ```
 *
 * @param props - {@link CountPillProps}
 * @returns React component
 */
export const CountPill = forwardRef<HTMLDivElement, CountPillProps>(
  ({ children, count, variant = 'filled', size = 'md' }, ref) => {
    const showCount = count !== undefined;

    return (
      <Box ref={ref} className={classes.root} data-size={size} data-variant={variant}>
        <Box className={classes.label} component="span">
          {children}
        </Box>
        {showCount && (
          <Box className={classes.count} component="span">
            {count}
          </Box>
        )}
      </Box>
    );
  },
);

CountPill.displayName = 'CountPill';
